"use client";
import { useState, useEffect, useCallback, useRef } from "react";
import { Images, Icons } from "@/app/assets/shared";
import Image from "next/image";
import React from "react";
import { getPortfolioDetailsByID } from "@/lib/ApiClient";
import PrimaryBtn from "@/components/PrimaryBtn";
import {
  portfolioModalInterface,
} from "@/lib/Types";
import SuccessStoryPopup from "@/app/success-stories/components/SuccessStoryPopup";
import { useScrollToContact } from "@/lib/ScrollUtils";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { ErrorType, SuccessStoryInterface } from "@/lib/Interface";

export default function SuccessStoryClient({ initialPortfolio }: SuccessStoryInterface) {
  const [portfolioArr] = useState(initialPortfolio); // ✅ No client fetch
  const [selectedPortfolio, setSelectedPortfolio] = useState(0);
  const [portfolioDetails, setPortfolioDetails] =
    useState<portfolioModalInterface>();
  const [expandedIds, setExpandedIds] = useState<Record<number, boolean>>({});
  const [isOverflowing, setIsOverflowing] = useState<Record<number, boolean>>(
    {},
  );
  const textRefs = useRef<Record<number, HTMLParagraphElement | null>>({});

  const fetchSelectedID = useCallback(async () => {
    if (!selectedPortfolio) return;
    try {
      const data = await getPortfolioDetailsByID(selectedPortfolio);
      setPortfolioDetails(data);
    } catch (error: ErrorType | any) {
      console.error(error?.message || "Failed to fetch details");
    }
  }, [selectedPortfolio]);

  useEffect(() => {
    fetchSelectedID();
  }, [fetchSelectedID]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const checkOverflow = () => {
      requestAnimationFrame(() => {
        const newOverflow: Record<number, boolean> = {};
        let hasChanged = false;

        portfolioArr.forEach((item) => {
          const element = textRefs.current[item.id];
          if (element) {
            const hasOverflow = element.scrollHeight > element.offsetHeight;

            // Only update if the value actually changed to avoid unnecessary re-renders
            if (hasOverflow !== isOverflowing[item.id]) {
              hasChanged = true;
            }
            newOverflow[item.id] = hasOverflow;
          }
        });

        if (hasChanged) {
          setIsOverflowing(newOverflow);
        }
      });
    };

    const debouncedCheck = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkOverflow, 150);
    };

    const resizeObserver = new ResizeObserver(debouncedCheck);

    const initialTimeout = setTimeout(checkOverflow, 300);

    portfolioArr.forEach((item) => {
      const el = textRefs.current[item.id];
      if (el) resizeObserver.observe(el);
    });

    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [portfolioArr, isOverflowing]);

  const toggleReadMore = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSmoothScroll = useScrollToContact();
  const isMobile = useIsMobile()

  return (
    <>
      {selectedPortfolio !== 0 ? (
        <SuccessStoryPopup
          onClose={() => setSelectedPortfolio(0)}
          portfolioDetails={portfolioDetails}
        />
      ) : null}

      <section className="success-story bg-[#000000] w-full relative overflow-hidden">
        <div className="texture-bg mix-blend-overlay  w-full h-full absolute top-0 left-0 z-0">
          {!isMobile &&
            <div className="absolute inset-0 z-0">
              <Image
                src={Images?.texture}
                alt="texture"
                fill
                className="object-cover mix-blend-overlay opacity-60"
                sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
              />
            </div>}
        </div>
        <div className="w-[907.05px] h-[907.05px] bg-[#225bd4]/40 rounded-full blur-[333.68px] absolute right-0 top-0 transform translate-x-1/3 -translate-y-1/3" />
        <div className="w-[907.05px] h-[907.05px] bg-[#225bd4]/40 rounded-full blur-[333.68px] absolute left-0 bottom-0 -translate-x-1/3 translate-y-1/3" />
        <div className="max-w-[5000px] mx-auto w-[calc(100%_-_15%)] py-[50px] lg:py-[150px] relative z-1">
          <p className="self-stretch text-[#fff] text-base font-medium uppercase tracking-tight pb-3 lg:pb-8 min-w-[17ch]">
            Our Success Stories
          </p>
          <div className="success-story-header flex flex-col lg:flex-row items-start justify-between gap-[15px]">
            <div className="w-full lg:w-[60%] success-story-header-left leading-[110%]">
              <h2 className="self-stretch text-[length:var(--section-title)] font-medium clash-font leading-[110%]">
                <span className="text-[#fff] capitalize">
                  Your Idea, Our Expertise:
                </span>
              </h2>
              <h2 className="self-stretch text-[length:var(--section-title)] font-medium clash-font leading-[110%]">
                <span className="text-[#225BD4] capitalize">
                  Our Success Stories
                </span>
              </h2>
            </div>
            <div className="success-story-header-right flex flex-col gap-[40px]">
              <div className="w-full max-w-[55ch] text-[#fff]/70 text-[length:var(--hero-para)] font-normal leading-[150%]">
                Our work speaks louder than words. We collaborate with visionary
                brands to solve complex challenges and deliver measurable
                outcomes.
              </div>
              <div className="w-fit">
                <PrimaryBtn
                  text="Schedule a Meeting"
                  iconSrc={Icons?.chevronBlue}
                  dark={true}
                  onClick={handleSmoothScroll}
                />
              </div>
            </div>
          </div>

          <div className="success-story-content grid grid-cols-1 2xl:grid-cols-2 gap-[60px] pt-[20px] lg:pt-[100px]">
            {portfolioArr?.length > 0 &&
              portfolioArr?.map((item: any, index: number) => {
                const id = item.id;
                const isExpanded = !!expandedIds[id];
                const isOverflow = isOverflowing[id];
                const shouldShowButton = isOverflow || isExpanded;

                const imageSrc =
                  index === 0
                    ? item?.homepageBg
                    : (item?.newSliderBg as string);

                return (
                  <div
                    key={id}
                    className={`success-card col-span-${index === 0 ? "2" : "1"} cursor-pointer flex flex-col ${item?.colspan === 1 ? "perspective-[1000px]" : ""
                      }`}
                    onClick={() => setSelectedPortfolio(id)}
                  >
                    {/* IMAGE */}
                    <div
                      className={`image-container group relative overflow-hidden w-full transition-transform duration-600 ease-in-out ${index === 0
                        ? "lg:aspect-[16/7] aspect-[1/1]"
                        : "2xl:aspect-[1/1] aspect-[1/1] lg:aspect-[16/7] hover:rotate-y-[-6deg] hover:scale-[0.95]"
                        }`}
                      style={{ transformStyle: "preserve-3d" }}
                    >
                      {imageSrc && (
                        <Image
                          src={imageSrc}
                          alt={item?.title || "portfolio"}
                          fill
                          priority={index === 0}
                          loading={index === 0 ? "eager" : "lazy"}
                          sizes="(max-width: 1024px) 100vw, 50vw"
                          className="object-cover transition-transform duration-600"
                        />
                      )}

                      {/* Arrow Button */}
                      <div className="size-12 bottom-[20px] right-[20px] bg-white arrow-button absolute rounded-full overflow-hidden flex transition-all duration-600 ease-in-out group-hover:rotate-[-45deg] group-hover:bg-[#225BD4]">
                        <Image
                          className="size-[15px] object-cover invert m-auto transition duration-600 group-hover:invert-0"
                          src={Icons?.arrowLeft as any}
                          alt="arrow"
                          width={15}
                          height={15}
                          sizes="(max-width: 1024px) 100vw, 50vw"
                        />
                      </div>
                    </div>

                    {/* TITLE */}
                    <h3 className="pt-[30px] pb-[10px] text-white text-[length:var(--sucess-card-title)] font-medium clash-font capitalize leading-[110%]">
                      {item?.title}
                    </h3>

                    {/* DESCRIPTION */}
                    <div className="flex flex-col items-start w-full md:min-h-[180px]">
                      <p
                        ref={(el) => {
                          textRefs.current[id] = el;
                        }}
                        className={`text-white/50 text-xl font-medium leading-[180%] transition-all duration-300 ${!isExpanded ? "line-clamp-3 overflow-hidden" : ""
                          }`}
                      >
                        {item?.description}
                      </p>

                      {shouldShowButton && (
                        <button
                          onClick={(e) => toggleReadMore(e, id)}
                          className="mt-2 inline-flex items-center justify-center p-1 rounded-md text-[#225bd4] hover:bg-[#225bd4] hover:text-white transition-all"
                          aria-label={isExpanded ? "Read less" : "Read more"}
                        >
                          {isExpanded ? (
                            <svg
                              aria-hidden="true"
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="m18 15-6-6-6 6" />
                            </svg>
                          ) : (
                            <svg
                              aria-hidden="true"
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="m6 9 6 6 6-6" />
                            </svg>
                          )}
                        </button>
                      )}
                    </div>

                    {/* TAGS */}
                    {item?.categories?.length > 0 && (
                      <div className="tags-container flex flex-wrap lg:flex-nowrap gap-[10px] lg:gap-[2px] py-[15px]">
                        {item.categories?.map((cat: any) => (
                          <div
                            key={cat.id || cat.name}
                            className="tag-item flex items-center justify-center h-[54px] px-6 text-white font-medium text-center bg-[#FFFFFF14] rounded-2xl lg:rounded-none lg:first:rounded-l-full lg:first:pl-10 lg:last:rounded-r-full lg:last:pr-10"
                          >
                            {cat?.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </section>
    </>
  );
}
